import { xml2js } from 'xml-js';
import type { Element } from 'xml-js';
import { buildAst } from '@xml-tools/ast';
import type { XMLDocument } from '@xml-tools/ast';
import { parse } from '@xml-tools/parser';
import type { DocumentCstNode } from '@xml-tools/parser';
import i18next from 'i18next';
import type {
    MacrosXMLPath,
    ExtensionLogger,
    FileData,
    SchemaDefinition,
    MacrosSchemaMetadata
} from '@sap/ux-specification-types';
import { DefinitionName } from '@sap/ux-specification-types';
import { LogSeverity } from '@sap/ux-specification-types';
import { getDefinitionKey, getErrorMessage } from '../../common/utils';
import { log } from '../../../extensionLogger';
import type { Definition } from 'typescript-json-schema';

export interface TraverseData<T = {}> {
    fullSchema: SchemaDefinition;
    schema: SchemaDefinition;
    element: Element;
    contextPath: MacrosXMLPath;
    config: object;
    /** Indicates if all properties of an element were removed during traversal. */
    removed?: boolean;
    /** Reference to the parent traversal data. */
    parent?: TraverseData<T>;
    /** Reference to the parent XML element. */
    parentElement?: Element;
    /** Custom data for traversal. */
    data?: T;
}

export type TraverseCallback<T = {}> = (
    traverseData: TraverseData<T>,
    property: SchemaDefinition,
    config: object,
    name: string
) => boolean;

/**
 * Method finds element by XML path.
 *
 * @param {Element} element Element to look in.
 * @param {MacrosXMLPath} path XML path to search.
 * @returns {Element | undefined} Element is returned if element found by passed path.
 */
function findElementByXMLPath(element: Element, path: MacrosXMLPath): Element | undefined {
    let currentElement: Element | undefined = element;
    for (const subPath of path) {
        const elements = currentElement?.elements?.filter((element) => element.type !== 'comment');
        currentElement = elements?.[subPath.index];
    }
    return currentElement;
}

/**
 * Method ensures that inner object exists for passed property name.
 *
 * @param {object} obj Object to check and update.
 * @param {string} name Property name in object to check and update.
 * @returns {boolean} True if object created.
 */
function ensureObjectExists(obj: object, name: string): boolean {
    if (!obj[name]) {
        obj[name] = {};
        return true;
    }
    return false;
}

/**
 * Method gets properties from full schema definitions using current schema $ref property.
 *
 * @param {SchemaDefinition} fullSchema Full schema object.
 * @param {string} $ref $ref property of current schema object.
 * @returns {Definition} The properties extracted from the schema definition referenced by $ref.
 */
function getPropertiesFromRef(fullSchema: SchemaDefinition, $ref: string): Definition {
    let properties: Definition = {};
    const key = getDefinitionKey($ref);
    if (fullSchema.definitions && fullSchema.definitions[key]) {
        const definition = fullSchema.definitions[key];
        if (typeof definition === 'object' && definition.properties) {
            properties = definition.properties;
        }
    }
    return properties;
}

/**
 * Method traverses schema for default aggregation.
 *
 * @param {TraverseData} traverseData Traverse data object.
 * @param {TraverseCallback} callback Callbck to handle export or import.
 * @param {MacrosSchemaMetadata} metadata Metadata with default aggregation.
 * @param {TraverseCallback} [onElementDeleteCallback]  - Callback invoked when an element is deleted during traversal.
 * @returns {boolean} Is changes applied during export or import.
 */
function traverseDefaultAggregation<T = {}>(
    traverseData: TraverseData,
    callback: TraverseCallback,
    metadata: MacrosSchemaMetadata,
    onElementDeleteCallback?: TraverseCallback<T>
): boolean {
    const { defaultAggregation } = metadata;
    const { schema, element, contextPath, config = {}, fullSchema, parentElement } = traverseData;
    const { properties = {} as Definition } = schema;
    const differs = ensureObjectExists(config, defaultAggregation);
    const contextConfig = config[defaultAggregation];
    return (
        traverseSchema(
            {
                fullSchema,
                schema: properties[defaultAggregation],
                element: element,
                contextPath: contextPath,
                config: contextConfig,
                parent: traverseData,
                data: traverseData.data,
                parentElement
            },
            callback,
            onElementDeleteCallback
        ) || differs
    );
}

/**
 * Method checks if passed schema is eligable for default aggregation handling.
 *
 * @param {Definition} properties Schema properties which can contain default aggregation.
 * @param {MacrosSchemaMetadata} [metadata] Metadata with default aggregation.
 * @returns {boolean} True if passed schema is eligable for default aggregation handling.
 */
function isDefaultAggregationAppliable(properties: Definition, metadata?: MacrosSchemaMetadata): boolean {
    return (
        metadata?.defaultAggregation && properties[metadata.defaultAggregation]?.['metadata']?.['path']?.length === 0
    );
}

/**
 * Method traverses schema together with XML element.
 *
 * @param {TraverseData} traverseData Traverse data object.
 * @param {TraverseCallback} callback Callbck to handle export or import.
 * @param {TraverseCallback} [onElementDeleteCallback]  - Callback invoked when an element is deleted during traversal.
 * @returns {boolean} Is changes applied during export or import.
 */
export function traverseSchema<T = {}>(
    traverseData: TraverseData<T>,
    callback: TraverseCallback<T>,
    onElementDeleteCallback?: TraverseCallback<T>
): boolean {
    const { schema, element, contextPath, config = {}, fullSchema } = traverseData;
    let differs = false;
    let { properties = {} as Definition } = schema;
    const { metadata = {} } = schema;
    if (schema.$ref && !schema.properties) {
        properties = getPropertiesFromRef(fullSchema, schema.$ref);
    }
    if (schema['metadata']?.['path']?.length && traverseData.removed === true) {
        differs = onElementDeleteCallback?.(traverseData, schema, traverseData.config, element.name) ?? differs;
    }

    for (const name in properties) {
        const property = properties[name];
        let shouldTraverse = typeof property === 'object';
        // Handle default aggregation
        if (
            shouldTraverse &&
            metadata?.defaultAggregation === name &&
            isDefaultAggregationAppliable(properties, metadata)
        ) {
            // Special hook to apply default aggregation
            differs = traverseDefaultAggregation(traverseData, callback, metadata, onElementDeleteCallback) || differs;
            shouldTraverse = false;
        }
        if (!shouldTraverse) {
            continue;
        }
        // Check if object contains "metadata" to map with XML element
        if (property['metadata']?.['path']?.length) {
            const path = property['metadata']['path'];
            const checkPath = path.slice(contextPath.length);
            // Find target element at the end of the path
            const targetElement = findElementByXMLPath(element, checkPath);
            // Find the parent element (one level up from target) for deletion operations
            // Parent is needed when elements become empty after attribute removal and need to be deleted from their container
            const parentElement = findElementByXMLPath(element, checkPath.slice(0, -1));
            const originalConfig = { ...config };
            differs = ensureObjectExists(config, name) || differs;
            const contextConfig = config[name];

            differs =
                traverseSchema(
                    {
                        fullSchema: traverseData.fullSchema,
                        schema: property,
                        element: targetElement,
                        contextPath: path,
                        config: contextConfig,
                        removed: originalConfig[name] === undefined,
                        parent: traverseData,
                        data: traverseData.data,
                        parentElement
                    },
                    callback,
                    onElementDeleteCallback
                ) || differs;
        } else {
            differs = callback(traverseData, property, config, name) || differs;
        }
    }
    return differs;
}

/**
 * Method parses XML file and returns parsed object.
 *
 * @param {FileData} xmlFile XML file.
 * @param {boolean} providePositions - If true then parsing result will contain information about elements and attributes positions.
 * @param {ExtensionLogger} [logger] - Logger class for logging messages.
 * @returns {Element | undefined} Parsed XML.
 */
export function parseXML(xmlFile: FileData, providePositions: true, logger?: ExtensionLogger): XMLDocument | undefined;
export function parseXML(xmlFile: FileData, providePositions: false, logger?: ExtensionLogger): Element | undefined;
/**
 * Parses the provided XML file and returns a parsed object.
 *
 * @param xmlFile - The XML file to be parsed.
 * @param providePositions - If true, the parsing result will include element and attribute positions.
 * @param logger - Optional logger instance for logging messages.
 * @returns The parsed XML as an Element or XMLDocument, or undefined if parsing fails.
 */
export function parseXML(
    xmlFile: FileData,
    providePositions: boolean,
    logger?: ExtensionLogger
): Element | XMLDocument | undefined {
    let element: Element | XMLDocument;
    try {
        if (providePositions) {
            const { cst, tokenVector } = parse(xmlFile.fileContent);
            const xmlDocument = buildAst(cst as DocumentCstNode, tokenVector);
            if (!xmlDocument.rootElement) {
                // Throw exception than invalid xml string provided
                throw new Error('Invalid XML');
            }
            element = xmlDocument;
        } else {
            element = xml2js(xmlFile.fileContent) as Element;
        }
    } catch (error) {
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('XMLPARSEFAILURE', { error: getErrorMessage(error) }),
            location: {
                path: xmlFile.dataSourceUri
            }
        });
    }
    return element;
}

/**
 * Resolves definition key for custom extension fragment.
 *
 * @param {string} fragmentName Name of the fragment.
 * @returns {string} Definition key for custom extension fragment.
 */
export function getCustomExtensionFragmentDefinitionKey(fragmentName: string): string {
    return `${DefinitionName.CustomExtensionFragment}<${fragmentName}>`;
}

/**
 * Method find first renderable element for passed definition element.
 *
 * @param element Definition element.
 * @returns First renderable element.
 */
export function getRootElement(element?: Element): Element | undefined {
    return element.elements?.find((root) => root.type !== 'comment');
}

/**
 * Builds a namespace URI → prefix map from xmlns attribute entries.
 * Works with any source that provides [attributeName, value] pairs — both
 * xml-js Element (via Object.entries) and @xml-tools/ast XMLElement (via .map).
 *
 * @param {[string, string][]} entries Attribute key/value pairs to scan.
 * @returns {Record<string, string>} Map of namespace URI → prefix (empty string for default xmlns).
 */
export function getXmlnsDependencies(entries: [string, string][]): Record<string, string> {
    const deps: Record<string, string> = {};
    for (const [attr, value] of entries) {
        if (attr === 'xmlns') {
            deps[value] = '';
        } else if (attr.startsWith('xmlns:')) {
            deps[value] = attr.slice(6);
        }
    }
    return deps;
}

/**
 * Finds a child element whose local name (after the namespace prefix) matches {@link localName}
 * and whose `key` attribute matches {@link keyValue}.
 *
 * @param parent Parent XML element whose children are searched.
 * @param localName Local element name without namespace prefix (e.g. `"ColumnOverride"`).
 * @param keyValue Expected value of the child element's `key` attribute.
 * @returns The matching child element, or `undefined` if not found.
 */
export function findChildElementByLocalNameAndKey(
    parent: Element,
    localName: string,
    keyValue: string
): Element | undefined {
    return parent.elements?.find(
        (el) => el.name?.split(':').pop() === localName && el.attributes?.['key'] === keyValue
    );
}
